import {
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';
import ArticleMeta from '../../types/ArticleMeta';
import { CardGTMData } from '../ArticleCard/CardUtils';
import { Link as RouterLink } from 'react-router';
import {
  articleCardClickTarget,
  sendArticleCardClickToGTM,
} from '../../utilities/gtm';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';

/* eslint-disable-next-line import-x/no-named-as-default-member */
dayjs.extend(isToday);

interface NewsArticleCardProps {
  meta: ArticleMeta;
  gtmData: CardGTMData;
  heading: TypographyProps['component'];
}

export default function NewsArticleCard({
  meta,
  gtmData = {},
  heading = 'h2',
}: Readonly<NewsArticleCardProps>) {
  const { t } = useTranslation();

  return (
    <Paper
      component='article'
      variant='outlined'
      sx={{ paddingX: 2, paddingY: 1 }}
      elevation={0}
    >
      <Stack
        direction='row'
        sx={{
          marginBottom: 1,
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='overline'
          sx={{
            color: 'text.secondary',
            paddingY: 0.5,
          }}
        >
          {meta.categories.primary}
        </Typography>
        <Tooltip
          title={t('content.releasedOnDate', {
            date: dayjs(meta.releaseDate).format('D.M.YYYY HH:mm'),
          })}
          placement='top'
        >
          <Typography
            variant='overline'
            sx={{
              color: 'text.secondary',
              paddingY: 0.5,
            }}
          >
            {dayjs(meta.releaseDate).isToday()
              ? dayjs(meta.releaseDate).format('HH:mm')
              : dayjs(meta.releaseDate).format('D.M.YYYY HH:mm')}
          </Typography>
        </Tooltip>
      </Stack>
      <Link
        component={RouterLink}
        to={meta.getRoute()}
        title={meta.title}
        data-testid='article-card-link'
        onClick={() =>
          sendArticleCardClickToGTM(
            meta,
            articleCardClickTarget.ARTICLE,
            gtmData.location,
            gtmData.index,
          )
        }
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          justifyContent: 'space-between',
          textDecoration: 'none',
          color: 'text.primary',
          '&:hover': { color: 'primary.main' },
        }}
      >
        <Stack direction='column'>
          <Typography
            component={heading}
            variant='h4'
            sx={{
              marginBottom: 1,
              hyphens: 'auto',
              textWrap: 'balance',
            }}
          >
            {meta.title}
          </Typography>

          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              hyphens: 'auto',
              textWrap: 'balance',
              whiteSpace: 'pre-wrap',
            }}
          >
            {meta.description}
          </Typography>
        </Stack>
      </Link>
    </Paper>
  );
}
